/**
 * Typed API client. Same-origin `/api/v1` (Vite dev proxy / prod web server
 * proxy), cookie-based sessions, RFC-7807 error surfacing.
 */
import type {
  AdProjectDto,
  AssetDto,
  AuthResponse,
  CreateJobResponse,
  CreditsResponse,
  EstimateResponse,
  GenerationSpec,
  HomeContentDto,
  JobDto,
  JobGraphDto,
  ModelsResponse,
  PlansResponse,
  PublicModel,
  PresetDto,
  ProblemDoc,
  PromotionsResponse,
  PublicationDto,
  StatusResponse,
  TemplateDto,
  TemplatesResponse,
  UgcProjectData,
  VoiceDto,
} from '@vidply/shared';

const BASE = '/api/v1';

/** Thrown for every non-2xx — carries the RFC-7807 problem document. */
export class ApiError extends Error {
  constructor(public problem: ProblemDoc) {
    super(problem.detail ?? problem.title);
  }
  get status(): number {
    return this.problem.status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // NOTE: init spreads FIRST — headers must merge last so a caller's extra
  // headers (e.g. Idempotency-Key) never clobber Content-Type. The JSON
  // content type is only sent WITH a body (Fastify rejects empty JSON).
  const res = await fetch(BASE + path, {
    credentials: 'include',
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let problem: ProblemDoc;
    try {
      problem = (await res.json()) as ProblemDoc;
    } catch {
      problem = { type: 'about:blank', title: res.statusText, status: res.status };
    }
    throw new ApiError(problem);
  }
  return (await res.json()) as T;
}

export const api = {
  // auth
  signup: (body: { email: string; password: string; displayName?: string; locale?: string }) =>
    request<AuthResponse>('/auth/signup', { method: 'POST', body: JSON.stringify(body) }),
  login: (body: { email: string; password: string }) =>
    request<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  me: () => request<AuthResponse>('/auth/me'),
  // First-upload trust gate — idempotent server-side, returns the fresh user.
  acceptMediaAgreement: () => request<AuthResponse>('/auth/media-agreement', { method: 'POST' }),

  // registry & estimation
  models: (modality?: string) => request<ModelsResponse>(`/models${modality ? `?modality=${modality}` : ''}`),
  estimate: (spec: GenerationSpec) =>
    request<EstimateResponse>('/estimate', { method: 'POST', body: JSON.stringify({ spec }) }),

  // jobs & assets
  createJob: (spec: GenerationSpec, idempotencyKey?: string) =>
    request<CreateJobResponse>('/jobs', {
      method: 'POST',
      body: JSON.stringify({ spec }),
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    }),
  job: (id: string) => request<{ job: JobDto }>(`/jobs/${id}`),
  jobs: (limit = 50) => request<{ jobs: JobDto[] }>(`/jobs?limit=${limit}`),
  cancelJob: (id: string) => request<{ ok: true }>(`/jobs/${id}/cancel`, { method: 'POST' }),
  assets: (kind?: string) => request<{ assets: JobDto['assets'] }>(`/assets${kind ? `?kind=${kind}` : ''}`),
  deleteAsset: (id: string) => request<{ ok: true }>(`/assets/${id}`, { method: 'DELETE' }),
  provenance: (assetId: string) => request<{ provenance: unknown }>(`/assets/${assetId}/provenance`),

  // money
  credits: () => request<CreditsResponse>('/credits'),
  plans: () => request<PlansResponse>('/plans'),
  promotions: () => request<PromotionsResponse>('/promotions/active'),
  usage: () => request<{ usage: { day: string; jobs: number; credits: number }[] }>('/usage'),

  // community & content
  explore: (q?: string) => request<{ publications: PublicationDto[] }>(`/explore${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  publication: (id: string) => request<{ publication: PublicationDto }>(`/explore/${id}`),
  like: (id: string) => request<{ likes: number }>(`/explore/${id}/like`, { method: 'POST' }),
  publish: (assetId: string, title: string) =>
    request<{ publication: PublicationDto }>('/publications', { method: 'POST', body: JSON.stringify({ assetId, title }) }),
  homeContent: (locale: string) => request<HomeContentDto>(`/content/home?locale=${locale}`),

  // templates (E13)
  listTemplates: (filters?: { category?: string; modality?: 'image' | 'video' }) => {
    const qs = new URLSearchParams(
      Object.entries(filters ?? {}).filter((e): e is [string, string] => !!e[1]),
    ).toString();
    return request<TemplatesResponse>(`/templates${qs ? `?${qs}` : ''}`);
  },
  getTemplate: (id: string) => request<{ template: TemplateDto }>(`/templates/${id}`),
  generateFromTemplate: (
    id: string,
    body: { imageAssetId?: string; vars?: Record<string, string>; locale?: string },
    idempotencyKey?: string,
  ) =>
    request<{ jobId: string; templateId: string; estimate: { credits: number }; entitlementMode: 'metered' | 'unlimited' }>(
      `/templates/${id}/generate`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      },
    ),

  // studios (E05 UGC) & graphs
  ugcCreate: () => request<{ project: AdProjectDto }>('/ugc/projects', { method: 'POST' }),
  ugcList: () => request<{ projects: AdProjectDto[] }>('/ugc/projects'),
  ugcGet: (id: string) => request<{ project: AdProjectDto }>(`/ugc/projects/${id}`),
  ugcPatch: (id: string, data: Partial<UgcProjectData>) =>
    request<{ project: AdProjectDto }>(`/ugc/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  ugcScript: (id: string) => request<{ project: AdProjectDto }>(`/ugc/projects/${id}/script`, { method: 'POST' }),
  ugcEstimate: (id: string) =>
    request<{ estimate: { credits: number; perNode: Record<string, number> } }>(`/ugc/projects/${id}/estimate`),
  ugcGenerate: (id: string) =>
    request<{ project: AdProjectDto; graphId: string; estimateCredits: number }>(`/ugc/projects/${id}/generate`, { method: 'POST' }),
  graph: (id: string) => request<{ graph: JobGraphDto }>(`/graphs/${id}`),
  retryNode: (graphId: string, nodeKey: string) =>
    request<{ jobId: string; estimateCredits: number }>(`/graphs/${graphId}/nodes/${nodeKey}/retry`, { method: 'POST' }),
  voices: (dialect?: string) => request<{ voices: VoiceDto[] }>(`/voices${dialect ? `?dialect=${dialect}` : ''}`),
  presets: (kind?: 'avatar' | 'location') => request<{ presets: PresetDto[] }>(`/presets${kind ? `?kind=${kind}` : ''}`),

  // Characters (E07) & Cinema (E03)
  characters: () =>
    request<{ characters: { id: string; name: string; attributes: Record<string, string>; promptFragment: string; posterUrl: string | null; createdAt: string }[] }>('/characters'),
  characterCandidates: (attributes: Record<string, string>) =>
    request<{ jobId: string; estimateCredits: number }>('/characters/candidates', { method: 'POST', body: JSON.stringify({ attributes }) }),
  saveCharacter: (body: { name: string; attributes: Record<string, string>; candidateJobId: string; assetId: string }) =>
    request<{ character: { id: string; name: string } }>('/characters', { method: 'POST', body: JSON.stringify(body) }),
  deleteCharacter: (id: string) => request<{ ok: true }>(`/characters/${id}`, { method: 'DELETE' }),
  composeShot: (body: Record<string, unknown>) =>
    request<{ shotId: string; projectId: string; graphId: string; estimateCredits: number }>('/cinema/shots', { method: 'POST', body: JSON.stringify(body) }),
  cinemaShots: () =>
    request<{ shots: { id: string; prompt: string; characters: { id: string; name: string }[]; chips: Record<string, string | undefined>; modelId: string; aspect: string; durationSec: number; variants: number; graphId: string | null; graph: JobGraphDto | null; createdAt: string }[] }>('/cinema/shots'),

  // Faceless generator (E08)
  facelessScript: (topic: string, dialect: string) =>
    request<{ scenes: { text: string; visual: string }[] }>('/faceless/script', { method: 'POST', body: JSON.stringify({ topic, dialect }) }),
  facelessGenerate: (body: Record<string, unknown>) =>
    request<{ graphId: string; estimateCredits: number }>('/faceless/generate', { method: 'POST', body: JSON.stringify(body) }),

  // Supercomputer (E11)
  agentThreads: () => request<{ threads: { id: string; title: string; updatedAt: string }[] }>('/agent/threads'),
  agentNewThread: () => request<{ thread: { id: string } }>('/agent/threads', { method: 'POST' }),
  agentMessages: (threadId: string) =>
    request<{ messages: { id: string; role: string; content: string; toolCall: { name: string; args: { prompt: string; modelId: string }; estimateCredits: number; state: string; jobId?: string } | null; createdAt: string }[] }>(`/agent/threads/${threadId}`),
  agentSend: (threadId: string, text: string) =>
    request<{ message: { id: string; role: string; content: string; toolCall: { name: string; args: { prompt: string; modelId: string }; estimateCredits: number; state: string; jobId?: string } | null } }>(`/agent/threads/${threadId}/messages`, { method: 'POST', body: JSON.stringify({ text }) }),
  agentSettle: (messageId: string, approve: boolean) =>
    request<{ jobId?: string }>(`/agent/messages/${messageId}/settle`, { method: 'POST', body: JSON.stringify({ approve }) }),

  // API tokens (agent surfaces — E12)
  tokens: () =>
    request<{ tokens: { id: string; name: string; surface: string; scopes: string[]; lastUsedAt: string | null; createdAt: string }[] }>('/tokens'),
  createToken: (name: string, surface: 'mcp' | 'cli' | 'plugin') =>
    request<{ token: { id: string; name: string; surface: string; raw: string } }>('/tokens', {
      method: 'POST',
      body: JSON.stringify({ name, surface }),
    }),
  revokeToken: (id: string) => request<{ ok: true }>(`/tokens/${id}`, { method: 'DELETE' }),

  // Account lifecycle (E15 — v0.8)
  verifyRequest: () => request<{ ok: true }>('/auth/verify/request', { method: 'POST' }),
  verifyConfirm: (token: string) => request<{ ok: true }>('/auth/verify/confirm', { method: 'POST', body: JSON.stringify({ token }) }),
  passwordForgot: (email: string) => request<{ ok: true }>('/auth/password/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  passwordReset: (token: string, password: string) =>
    request<{ ok: true }>('/auth/password/reset', { method: 'POST', body: JSON.stringify({ token, password }) }),
  passwordChange: (current: string, next: string) =>
    request<{ ok: true }>('/auth/password/change', { method: 'POST', body: JSON.stringify({ current, next }) }),
  deleteAccount: (password: string) => request<{ ok: true }>('/auth/me', { method: 'DELETE', body: JSON.stringify({ password }) }),

  // Uploads (v0.8 — multipart, NOT json). Optional extra multipart fields
  // (e.g. trimStartSec/trimEndSec for Edit Video) are appended BEFORE the
  // file part so @fastify/multipart exposes them on `file.fields`.
  upload: async (file: File, fields?: Record<string, string>): Promise<{ asset: AssetDto }> => {
    const form = new FormData();
    for (const [name, value] of Object.entries(fields ?? {})) form.append(name, value);
    form.append('file', file);
    const res = await fetch('/api/v1/uploads', { method: 'POST', body: form, credentials: 'include' });
    if (!res.ok) {
      const problem = await res.json().catch(() => ({ title: 'Upload failed', status: res.status }));
      throw new ApiError({ type: 'about:blank', status: res.status, ...problem });
    }
    return (await res.json()) as { asset: AssetDto };
  },

  // Notifications (v0.8)
  notifications: () =>
    request<{ notifications: { id: string; level: string; messageKey: string; params: Record<string, unknown> | null; read: boolean; createdAt: string }[] }>(
      '/notifications',
    ),
  notificationsRead: () => request<{ ok: true }>('/notifications/read', { method: 'POST' }),

  // Admin (role-gated server-side)
  adminOverview: () => request<Record<string, unknown>>('/admin/overview'),
  adminModels: () => request<{ models: (PublicModel & { healthScore: number })[] }>('/admin/models'),
  adminLifecycle: (id: string, status: 'active' | 'degraded' | 'deprecated' | 'disabled') =>
    request<{ ok: true }>(`/admin/models/${id}/lifecycle`, { method: 'POST', body: JSON.stringify({ status }) }),
  adminComp: (email: string, opts?: { floor?: number; topUpTo?: number; revoke?: boolean }) =>
    request<{ comp?: Record<string, unknown>; ok?: boolean; removed?: number }>('/admin/users/comp', {
      method: 'POST',
      body: JSON.stringify({ email, ...opts }),
    }),

  // status
  status: () => request<StatusResponse>('/status'),

  // telemetry (fire-and-forget; failures are swallowed)
  telemetry: (events: { level: 'info' | 'warn' | 'error'; message: string; stack?: string; url?: string }[]) =>
    request<{ ok: true }>('/telemetry', { method: 'POST', body: JSON.stringify({ events }) }).catch(() => undefined),
};
